import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { X, Eye } from 'lucide-react';


export default function ImpersonationBanner() {
  const [impersonationInfo, setImpersonationInfo] = useState(null);
  const [exiting, setExiting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const checkImpersonation = useCallback(async () => {
    try {
      const response = await api.get('/admin/impersonation-status');
      if (response.data.is_impersonating) {
        setImpersonationInfo(response.data);
      } else {
        setImpersonationInfo(null);
      }
    } catch (error) {
      // Not impersonating or not admin - silently fail
      setImpersonationInfo(null);
    }
  }, []);

  // Check on mount and on route changes
  useEffect(() => {
    checkImpersonation();
  }, [location.pathname, checkImpersonation]);

  const handleExitImpersonation = async () => {
    setExiting(true);
    try {
      await api.post('/admin/stop-impersonation');
      setImpersonationInfo(null);
      // Redirect back to admin users page
      navigate('/admin/users');
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
    } finally {
      setExiting(false);
    }
  };

  if (!impersonationInfo) {
    return null;
  }

  return (
    <div
      className="fixed top-3 right-3 z-[100]"
      data-testid="impersonation-banner"
      title={`Viewing as ${impersonationInfo.target_name} (${impersonationInfo.target_email})`}
    >
      <Button
        onClick={handleExitImpersonation}
        disabled={exiting}
        size="sm"
        className="bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-lg rounded-full px-3 py-1 h-auto flex items-center gap-1.5"
        data-testid="exit-impersonation"
      >
        <Eye className="w-4 h-4" />
        <span className="hidden sm:inline">{exiting ? 'Exiting…' : 'Exit Proxy'}</span>
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}
